import { appCheckHeaders } from '../firebase';

// Empty string → relative URLs; Firebase Hosting rewrites route to Cloud Run.
// Set REACT_APP_BACKEND_URL only when running against a standalone backend (local dev).
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

export async function tenantFetch(
  path: string,
  token: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  // App Check token (no-op when App Check is not configured).
  const ac = await appCheckHeaders();
  for (const [k, v] of Object.entries(ac)) headers.set(k, v);
  return fetch(`${BACKEND_URL}${path}`, { ...init, headers });
}

export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export type InvoiceStatus = 'draft' | 'pending' | 'paid' | 'failed' | 'returned' | 'void';

export interface LineItem {
  label: string;
  amountCents: number;
  chargeId: string | null;
}

// Spec 034 — how the tenant's charge was derived. Every sub-field is optional:
// the backend omits fields whose underlying data is absent (legacy invoices
// degrade gracefully).
export interface InvoiceCalculation {
  totalBillCents?: number;
  allocationMethod?: string;
  tenantShareBasis?: string;
  tenantAmountCents?: number;
}

// Spec 034 — GET /tenants/me/invoices/:invoiceId/bill success shape.
export interface BillDocumentLink {
  url: string;
  expiresAt: string;
  contentType: string;
}

// Spec 035 — trash / refuse charge breakdown, present only when the invoice
// includes a trash line item. All sub-fields are optional where the backend
// lacks the underlying source data.
export interface TrashDetails {
  totalRefuseAmountCents: number;
  tenantShareCents: number;
  allocationMethod: string;
  period: { startDate: string; endDate: string };
  cityFormula?: string;
  canCount?: number;
}

// Spec 035 — GET /tenants/me/property-tax-bill/redacted success shape.
export interface RedactedBillLink {
  url: string;
  expiresAt: string;
  contentType: string;
}

export interface TenantInvoice {
  id: string;
  amount: number;
  totalAmountCents?: number;
  totalBillCents?: number;
  previousCycleAmountCents?: number;
  currency: string;
  description: string;
  status: InvoiceStatus;
  providerInvoiceId?: string;
  paymentUrl?: string | null;
  createdAt?: string | { seconds: number };
  unitId?: string;
  unitNumber?: string;
  lineItems?: LineItem[];
  propertyName?: string;
  propertyAddress?: string;
  tenantName?: string;
  period?: string;
  // Spec 034 — tenant transparency + disclosure fields.
  calculation?: InvoiceCalculation;
  billAvailable?: boolean;
  disclosure?: string;
  // Spec 035 — trash / refuse charge breakdown (present only when invoice has a trash line item).
  trashDetails?: TrashDetails;
  metadata: {
    measurementId: string;
    totalGallons?: number;
    tenantGallons?: number;
    tenantId: string;
    tenantUid: string | null;
    propertyId: string;
    shareBasis?: string;
  };
}
